import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { workspaceAPI } from 'features/workspace/api/workspace';
import { handleAPIError } from 'helpers/handleAPIError';
import { cloneDeep } from 'lodash';
import difference from 'lodash/difference';
import isEmpty from 'lodash/isEmpty';
import remove from 'lodash/remove';
import { normalize, schema } from 'normalizr';
import {
  bulkDeleteDependency,
  createDependencyFromString,
  selectDependencyEntities,
} from '../../dependencies/store/dependenciesSlice';
import {
  addFlowAITasksAPI,
  commitTaskAPI,
  createBaselineAPI,
  createProductionBoardTasksAPI,
  createTaskAPI,
  createTasksAPI,
  deleteBaselineAPI,
  deleteTasksAPI,
  fetchBaselineTasksAPI,
  fetchChildrenTasksAPI,
  fetchProjectBaselinesAPI,
  fetchTaskConstraintsAPI,
  fetchTasksAPI,
  insertRecipeAPI,
  jobwalkAPI,
  pauseTaskAPI,
  resumeTaskAPI,
  splitTaskByLBS,
  uncommitTaskAPI,
  updateBaselineAPI,
  updatePreconditionsAPI,
  updateProductionBoardTasksAPI,
  updateTasksAPI,
} from '../api/tasksService';

/**
 * Fetch tasks
 */
export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async ({ projectId, includes, taskType, queryParam = '' } = {}, { rejectWithValue }) => {
    const apiResponse = await fetchTasksAPI({
      projectId,
      includes,
      queryParam: taskType === 'lookahead' ? '&is_lookahead=1' + queryParam : queryParam,
    });

    if (apiResponse.error) {
      return rejectWithValue(apiResponse.response.errors);
    } else {
      const dependencySchema = new schema.Entity('dependencies');
      const taskSchema = new schema.Entity('task', {
        dependencies: [dependencySchema],
      });
      const formattedData = new schema.Array(taskSchema);

      const formattedResponse = normalize(apiResponse.response.data, formattedData);

      return formattedResponse;
    }
  }
);

/**
 * Create task
 * @param {Object} data
 * @param {String} data.name
 * @param {String} data.project_id
 */
export const createTask = createAsyncThunk(
  'tasks/createTask',
  async (data, { rejectWithValue }) => {
    const apiResponse = await createTaskAPI({
      ...data,
      project_id: data.project_id,
      optimal_crew: 1,
    });
    if (apiResponse?.error) {
      return rejectWithValue(apiResponse.response.errors);
    } else {
      return apiResponse?.response?.data ? apiResponse.response.data : {};
    }
  }
);

/**
 * Update tasks
 * @param {Object[]} data
 */
export const updateTasks = createAsyncThunk(
  'tasks/updateTasks',
  async (requestParameters, { rejectedWithValue }) => {
    const apiResponse = await updateTasksAPI(requestParameters);
    if (apiResponse.error) {
      return rejectedWithValue(apiResponse.error);
    } else {
      if (!isEmpty(apiResponse.response?.data)) {
        const dependencySchema = new schema.Entity('dependencies');
        const taskSchema = new schema.Entity('task', {
          dependencies: [dependencySchema],
        });
        const formattedData = new schema.Array(taskSchema);
        const formattedResponse = normalize(apiResponse.response.data, formattedData);
        return formattedResponse;
      }
      return apiResponse;
    }
  }
);

/** 
    Update fulfilled preconditions preconditionId 
    which are present will be marked as fulfilled
    @param {String} id taskId
    @param {[Number]} preconditionId 
*/
export const updatePreconditions = createAsyncThunk(
  'tasks/updatePreconditions',
  async (requestParameters, { rejectedWithValue }) => {
    const apiResponse = await updatePreconditionsAPI(requestParameters);
    if (apiResponse.error) {
      return rejectedWithValue(apiResponse.error);
    } else {
      const dependencySchema = new schema.Entity('dependencies');
      const taskSchema = new schema.Entity('task', {
        dependencies: [dependencySchema],
      });
      const formattedResponse = normalize(apiResponse.response.data, taskSchema);
      return formattedResponse;
    }
  }
);

/**
 * Create production board task
 */
export const createProductionBoardTasks = createAsyncThunk(
  'tasks/createProductionBoardTasks',
  async (data, { rejectWithValue }) => {
    const apiResponse = await createProductionBoardTasksAPI(data);
    return handleAPIError(apiResponse, rejectWithValue);
  }
);

export const updateProductionBoardTaskGroup = createAsyncThunk(
  'tasks/updateProductionBoardTaskGroup',
  async (data, { rejectWithValue }) => {
    const requestParameter = cloneDeep(data);
    delete data['removedTaskIds'];
    const apiResponse = await updateProductionBoardTasksAPI(requestParameter);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      if (apiResponse?.response?.data) {
        return {
          list: apiResponse?.response?.data,
          removedTaskIds: requestParameter.removedTaskIds,
        };
      } else {
        return { list: [], removedTaskIds: requestParameter.removedTaskIds };
      }
    }
  }
);

/**
 * Delete tasks
 * @param {Object[]} data
 */
export const deleteTasks = createAsyncThunk(
  'tasks/bulkDeleteTasks',
  async (requestParameters, { rejectWithValue }) => {
    const apiResponse = await deleteTasksAPI(requestParameters);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return requestParameters.tasks;
    }
  }
);

/**
 * Split Locations (location, area, zones) into tasks
 * @param {data}
 * @param {Number[]} data.tasks - task ids
 * @param {Number[]} data.areas - area ids
 */
export const splitLocationsIntoTasks = createAsyncThunk(
  'tasks/splitLocationsIntoTasks',
  async (data, { rejectWithValue }) => {
    const apiResponse = await splitTaskByLBS(data);

    if (apiResponse.error) {
      return rejectWithValue(apiResponse.response.errors);
    } else {
      const dependencySchema = new schema.Entity('dependencies');
      const taskSchema = new schema.Entity('task', {
        dependencies: [dependencySchema],
      });
      const formattedData = new schema.Array(taskSchema);
      const formattedResponse = normalize(apiResponse.response.data, formattedData);

      return formattedResponse;
    }
  }
);

/**
 * Fill task with recipe
 * @param {*} data
 * @param {Number} data.recipe_id - recipeId
 * @param {Number} data.taskId - taskId
 */

export const insertRecipe = createAsyncThunk(
  'tasks/insertRecipe',
  async ({ task_id, recipe_id, project_id }, { rejectWithValue }) => {
    const apiResponse = await insertRecipeAPI({ task_id, recipe_id, project_id });
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      const dependencySchema = new schema.Entity('dependencies');
      const taskSchema = new schema.Entity('task', {
        dependencies: [dependencySchema],
      });
      const formattedData = new schema.Array(taskSchema);
      const formattedResponse = normalize(apiResponse.response.data, formattedData);

      return formattedResponse;
    }
  }
);

/**
 * Commit task
 * @param {data}
 * @param {Number} data.id - task id
 * @param {Date} [data.start] - task committed start date
 */
export const commit = createAsyncThunk('tasks/commit', async (data, { rejectWithValue }) => {
  const apiResponse = await commitTaskAPI({ id: data.id, start: data.start });
  if (apiResponse?.error) {
    return rejectWithValue(apiResponse.response.errors);
  } else {
    return apiResponse?.response?.data;
  }
});

/**
 * Uncommit task
 * @param {data}
 * @param {Number} data.id - task id
 * @param {Date} [data.start] - task committed start date
 */
export const uncommit = createAsyncThunk('tasks/uncommit', async (data, { rejectWithValue }) => {
  const apiResponse = await uncommitTaskAPI({ id: data.id });
  if (apiResponse?.error) {
    return rejectWithValue(apiResponse.response.errors);
  } else {
    return apiResponse?.response?.data;
  }
});

export const fetchTaskConstraints = createAsyncThunk(
  'tasks/fetchTaskConstraints',
  async (taskId, { rejectWithValue }) => {
    const apiResponse = await fetchTaskConstraintsAPI(taskId);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

/**
 * Used while importing task from file
 */
export const uploadTasks = createAsyncThunk(
  'tasks/uploadTasks',
  async (requestParameters, { rejectWithValue }) => {
    const apiResponse = await createTasksAPI(requestParameters);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      const dependencySchema = new schema.Entity('dependencies');
      const taskSchema = new schema.Entity(
        'task',
        {
          dependencies: [dependencySchema],
        },
        {
          idAttribute: 'import_id',
        }
      );
      const formattedData = new schema.Array(taskSchema);
      const formattedResponse = normalize(apiResponse.response.data, formattedData);
      return { ...formattedResponse, apiData: apiResponse.response.data };
    }
  }
);

/**
 * Fetches children tasks
 * @param {Object} requestParameters
 * @param {Array} requestParameters.projectIds project ids [121,107]
 * @param {Array} requestParameters.taskIds taskIds [121,107]
 * @returns
 */
export const fetchChildrenTasks = createAsyncThunk(
  'tasks/fetchChildrenTasks',
  async (requestParameters, { rejectWithValue }) => {
    const apiResponse = await fetchChildrenTasksAPI(requestParameters);

    if (apiResponse.error) {
      return rejectWithValue(apiResponse.response.errors);
    } else {
      const dependencySchema = new schema.Entity('dependencies');
      const taskSchema = new schema.Entity('task', {
        dependencies: [dependencySchema],
      });
      const formattedData = new schema.Array(taskSchema);
      const formattedResponse = normalize(apiResponse.response.data, formattedData);
      return formattedResponse;
    }
  }
);

/**
 * Fetch Baselines
 * @param {Number} projectId
 * @returns
 */

export const fetchBaselines = createAsyncThunk(
  'tasks/fetchBaselines',
  async (projectId, { rejectWithValue }) => {
    const apiResponse = await fetchProjectBaselinesAPI(projectId);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.response.errors);
    } else {
      const baselineSchema = new schema.Entity('baselines');
      const formattedData = new schema.Array(baselineSchema);
      const formattedResponse = normalize(apiResponse.response.data, formattedData);
      return formattedResponse?.entities?.baselines;
    }
  }
);

/**
 * Create or update baselines
 * @param {Object} requestParameter
 * @param {Number} requestParameter.id
 * @param {String} requestParameter.name
 * @returns
 */
export const saveBaseline = createAsyncThunk(
  'tasks/saveBaseline',
  async (requestParameter, { rejectWithValue }) => {
    const apiResponse = requestParameter.id
      ? await updateBaselineAPI(requestParameter)
      : await createBaselineAPI(requestParameter);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.response.errors);
    } else {
      return apiResponse.response.data;
    }
  }
);

/**
 * Delete baseline
 * @param {Number} id
 * @returns
 */
export const deleteBaseline = createAsyncThunk(
  'tasks/deleteBaseline',
  async (id, { rejectWithValue }) => {
    const apiResponse = await deleteBaselineAPI(id);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.response.errors);
    } else {
      return apiResponse.response.data;
    }
  }
);

/**
 * Get baseline tasks
 * @param {Number} id
 * @returns
 */
export const fetchBaselineTasks = createAsyncThunk(
  'tasks/fetchBaselineTasks',
  async (id, { rejectWithValue }) => {
    const apiResponse = await fetchBaselineTasksAPI(id);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.response.errors);
    } else {
      return apiResponse.response.data;
    }
  }
);

/**
 * Add JobWalk
 * @param {Object} requestParameter
 * @param {String} requestParameter.id
 * @param {Number} requestParameter.percent_complete
 * @param {Date} requestParameter.date
 */
export const jobwalk = createAsyncThunk(
  'tasks/jobwalk',
  async (requestParameter, { rejectWithValue }) => {
    const apiResponse = await jobwalkAPI(requestParameter);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

/**
 * Pause / Resume task
 * @param {Object} requestParameter
 * @param {String} requestParameter.paused_on
 * @param {Number} requestParameter.reason
 * @param {Date} requestParameter.resume_on
 * @param {String} requestParameter.type
 * @param {Date} requestParameter.resumed_on
 */
export const pauseResumeTask = createAsyncThunk(
  'tasks/pauseResumeTask',
  async (requestParameter, { rejectWithValue }) => {
    const apiResponse =
      requestParameter.type === 'pause'
        ? await pauseTaskAPI(requestParameter)
        : await resumeTaskAPI(requestParameter);
    return handleAPIError(apiResponse, rejectWithValue);
  }
);

/**
 * Add Flow AI Tasks
 * @param {*} data
 * @param {Number} data.recipe_id
 * @param {Number} data.task_id
 * @param {Object} data.locations
 * @param {Object} data.zones
 * @param {Object} data.areas
 */
export const addFlowAITasks = createAsyncThunk(
  'tasks/addFlowAITasks',
  async (data, { rejectWithValue }) => {
    const apiResponse = await addFlowAITasksAPI(data);

    if (apiResponse.error) {
      return rejectWithValue(apiResponse.response.errors);
    } else {
      const dependencySchema = new schema.Entity('dependencies');
      const taskSchema = new schema.Entity('task');
      taskSchema.define({ children: [taskSchema], dependencies: [dependencySchema] });

      const formattedData = new schema.Array(taskSchema);
      const formattedResponse = normalize(apiResponse.response.data, formattedData);

      return formattedResponse;
    }
  }
);

const tasksAdapter = createEntityAdapter({
  sortComparer: (prev, next) => prev.display_order - next.display_order,
});

const initialState = tasksAdapter.getInitialState({
  loading: false,
  error: null,
  isUpdated: false,
  predecessorDialog: {
    open: false,
    taskId: null,
  },
  baselines: {
    list: [],
    selected: null,
    tasks: [],
  },
  customViews: {
    list: {},
  },
});

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    clearTasks: (state, action) => {
      tasksAdapter.removeAll(state);
    },
    setPredecessorDialog: (state, action) => {
      state.predecessorDialog = action.payload;
    },
    clearSelectedBaseline: (state, action) => {
      state.baselines.selected = null;
      state.baselines.tasks = [];
    },
    saveTask: (state, action) => {
      tasksAdapter.upsertOne(state, { ...action.payload });
    },
    deleteTask: (state, action) => {
      tasksAdapter.removeOne(state, action.payload.id);
    },
  },
  extraReducers: (builder) => {
    /** Fetch Tasks */
    builder.addCase(fetchTasks.fulfilled, (state, action) => {
      if (action.payload?.entities?.task) {
        tasksAdapter.setAll(state, action.payload?.entities?.task);
      } else {
        tasksAdapter.setAll(state, {});
      }
    });
    builder.addCase(fetchTasks.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(fetchTasks.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });

    // Create tasks
    builder.addCase(createTask.fulfilled, (state, action) => {
      tasksAdapter.removeOne(state, action.meta.arg.id);
      tasksAdapter.upsertOne(state, { ...action.payload, dependencies: [] });
    });

    builder.addCase(createProductionBoardTasks.fulfilled, (state, action) => {
      tasksAdapter.upsertMany(state, action.payload);
    });

    // Save Dependency
    builder.addCase('dependencies/saveDependency', (state, action) => {
      if (
        action.payload?.isNew &&
        !state.entities[action.payload.taskId]?.dependencies.includes(action.payload?.id)
      ) {
        state.entities[action.payload.taskId]?.dependencies
          ? state.entities[action.payload.taskId].dependencies.push(action.payload.id)
          : (state.entities[action.payload.taskId].dependencies = [action.payload.id]);
      }
    });

    // Add Dependency
    builder.addCase('dependencies/addDependency/fulfilled', (state, action) => {
      if (action.payload?.data) {
        const target = state.entities[action.payload.data.target];
        target.dependencies ??= [];
        target.dependencies.push(action.payload.data.id);
      }
    });

    // Delete Dependency
    builder.addCase('dependencies/deleteDependency', (state, action) => {
      const dependencies = state.entities[action.payload.taskId]?.dependencies;
      remove(dependencies, (dependencyId) => dependencyId == action.payload.id);
      state.entities[action.payload.taskId].dependencies = dependencies;
    });

    builder.addCase(createDependencyFromString.fulfilled, (state, action) => {
      if (action.payload?.affected_tasks) {
        tasksAdapter.upsertMany(state, action.payload.affected_tasks);
      }
      if (action.payload?.created_dependencies) {
        action.payload.created_dependencies.forEach((dep) =>
          state.entities[action.meta.arg.id].dependencies.push(dep.id)
        );
      }
      if (action.payload?.deleted_dependencies) {
        action.payload.deleted_dependencies.forEach((dep) =>
          remove(
            state.entities[action.meta.arg.id].dependencies,
            (dependencyId) => dependencyId === dep.id
          )
        );
      }
      state.xArrowUpdate = !state.xArrowUpdate;
    });

    // Sync Dependency
    builder.addCase('dependencies/syncDependency/fulfilled', (state, action) => {
      if (action.payload?.affected_tasks) {
        tasksAdapter.upsertMany(state, action.payload.affected_tasks);
      }
      if (action.payload?.created_dependencies) {
        action.payload.created_dependencies.forEach((dep) =>
          state.entities[action.meta.arg.taskId].dependencies.push(dep.id)
        );
      }
      if (action.payload?.deleted_dependencies) {
        action.payload.deleted_dependencies.forEach((dep) =>
          remove(
            state.entities[action.meta.arg.taskId].dependencies,
            (dependencyId) => dependencyId === dep.id
          )
        );
      }
      if (action.payload?.removeIds) {
        action.payload?.removeIds.forEach((removeId) =>
          remove(
            state.entities[action.meta.arg.taskId].dependencies,
            (dependencyId) => dependencyId === removeId
          )
        );
      }
    });

    // Commit task
    builder.addCase(commit.fulfilled, (state, action) => {
      if (action.payload?.updated_tasks) {
        tasksAdapter.upsertOne(state, action.payload.updated_tasks);
      }
      if (action.payload?.affected_tasks) {
        tasksAdapter.updateMany(state, action.payload.affected_tasks);
      }
    });
    builder.addCase(commit.rejected, (state, action) => {
      state.error = action.payload;
    });
    builder.addCase(uncommit.fulfilled, (state, action) => {
      if (action.payload?.updated_tasks) {
        tasksAdapter.upsertOne(state, action.payload.updated_tasks);
      }
      if (action.payload?.affected_tasks) {
        tasksAdapter.updateMany(state, action.payload.affected_tasks);
      }
    });
    builder.addCase(uncommit.rejected, (state, action) => {
      state.error = action.payload;
    });

    // Fill tasks with recipes
    builder.addCase(insertRecipe.fulfilled, (state, action) => {
      tasksAdapter.upsertMany(state, action.payload.entities.task);
      if (action.meta.arg.task_id) {
        state.entities[action.meta.arg.task_id].children
          ? state.entities[action.meta.arg.task_id].children.push(...action.payload.result)
          : (state.entities[action.meta.arg.task_id]['children'] = action.payload.result);
      }
    });

    // Fetch Constraints
    builder.addCase(fetchTaskConstraints.fulfilled, (state, action) => {
      const id = action.meta.arg;
      tasksAdapter.updateOne(state, { id: id, changes: { preconditions: action.payload } });
    });
    builder.addCase(fetchTaskConstraints.rejected, (state, action) => {
      state.error = action.payload;
    });

    //Split Locations
    builder.addCase(splitLocationsIntoTasks.fulfilled, (state, action) => {
      if (action.payload?.entities?.task) {
        action.meta.arg.tasks.forEach((id) => {
          const newChildren = [
            ...action.payload.result.filter(
              (childId) => action.payload.entities.task[childId].parent_id === id
            ),
          ];
          state.entities[id].children
            ? state.entities[id].children.push(newChildren)
            : (state.entities[id].children = newChildren);
        });
        tasksAdapter.upsertMany(state, action.payload.entities.task);
      }
    });
    builder.addCase(splitLocationsIntoTasks.rejected, (state, action) => {
      state.error = action.payload;
    });

    builder.addCase(updateTasks.fulfilled, (state, action) => {
      try {
        if (action.payload?.entities?.task) {
          tasksAdapter.upsertMany(
            state,
            Object.values(action.payload?.entities?.task)?.map((task) => ({
              ...task,
              loading: false,
            }))
          );
        } else {
          if (action.meta?.arg?.length) {
            tasksAdapter.upsertMany(
              state,
              action.meta?.arg?.map((task) => ({
                ...task,
                loading: false,
              }))
            );
          }
        }
        state.isUpdated = true;
      } catch (error) {
        console.log('Error =>', error);
      }
    });

    // Update Preconditions
    builder.addCase(updatePreconditions.pending, (state, action) => {
      if (action.meta?.arg?.id) {
        tasksAdapter.upsertOne(state, {
          id: action.meta.arg.id,
          loading: true,
        });
      }
    });

    builder.addCase(updatePreconditions.fulfilled, (state, action) => {
      if (action.payload?.entities?.task && action.payload?.result) {
        tasksAdapter.upsertOne(state, {
          ...action.payload?.entities?.task[action.payload?.result],
          loading: false,
        });
      }
    });

    // Delete tasks
    builder.addCase(deleteTasks.fulfilled, (state, action) => {
      if (action.payload) {
        tasksAdapter.removeMany(state, action.payload);
      }
    });

    // Bulk delete dependency
    builder.addCase(bulkDeleteDependency.fulfilled, (state, action) => {
      if (action.payload?.taskData) {
        action.payload.taskData.forEach(({ taskId, dependencyId }) => {
          if (taskId && state.entities[taskId]) {
            const dependencies = state.entities[taskId].dependencies;
            remove(dependencies, (depId) => depId == dependencyId);
            if (state.entities[taskId]) {
              state.entities[taskId].dependencies = dependencies;
            }
          }
        });
      }
    });

    // Bulk upload tasks
    builder.addCase(uploadTasks.fulfilled, (state, action) => {
      if (action.payload?.entities?.task) {
        tasksAdapter.upsertMany(state, Object.values(action.payload.entities.task));
      }
    });

    builder.addCase(fetchChildrenTasks.fulfilled, (state, action) => {
      if (action.payload?.entities?.task) {
        tasksAdapter.upsertMany(state, Object.values(action.payload.entities.task));
      }
    });

    builder.addCase(fetchBaselines.fulfilled, (state, action) => {
      state.baselines.list = isEmpty(action.payload) ? {} : action.payload;
    });

    builder.addCase(saveBaseline.fulfilled, (state, action) => {
      state.baselines.list[action.payload.id] = action.payload;
    });

    builder.addCase(deleteBaseline.fulfilled, (state, action) => {
      if (action.meta.arg) {
        delete state.baselines.list[action.meta.arg];
      }
    });

    builder.addCase(fetchBaselineTasks.fulfilled, (state, action) => {
      state.baselines.selected = action.payload;
      state.baselines.tasks = action.payload?.tasks;
    });

    // Jobwalk
    builder.addCase(jobwalk.pending, (state, action) => {
      tasksAdapter.upsertOne(state, {
        id: action.meta?.arg?.id,
        loading: true,
      });
    });

    builder.addCase(jobwalk.fulfilled, (state, action) => {
      if (action.payload?.task) {
        tasksAdapter.upsertOne(state, {
          ...action.payload.task,
          loading: false,
        });
      }
    });

    builder.addCase(jobwalk.rejected, (state, action) => {
      tasksAdapter.upsertOne(state, {
        id: action.meta?.arg?.id,
        loading: false,
      });
    });

    // Pause resume task
    builder.addCase(pauseResumeTask.pending, (state, action) => {
      tasksAdapter.upsertOne(state, {
        id: action.meta?.arg?.id,
        loading: true,
      });
    });

    builder.addCase(pauseResumeTask.fulfilled, (state, action) => {
      tasksAdapter.upsertOne(state, { ...action.payload, loading: false });
    });

    builder.addCase(pauseResumeTask.rejected, (state, action) => {
      tasksAdapter.upsertOne(state, {
        id: action.meta?.arg?.id,
        loading: false,
      });
    });

    builder.addCase(updateProductionBoardTaskGroup.fulfilled, (state, action) => {
      if (action.payload?.list) {
        tasksAdapter.upsertMany(state, action.payload.list);
      }
      if (action.payload?.removedTaskIds && action.payload?.removedTaskIds.length) {
        tasksAdapter.removeMany(state, action.payload.removedTaskIds);
      }
    });

    // builder.addCase(bulkUpdateTaskCompanies.fulfilled, (state, action) => {
    //   if (action?.meta?.arg?.length) {
    //     const tasksToUpdate = action.meta.arg.map(({ id, crew_size }) => ({ id, crew_size }));
    //     tasksAdapter.upsertMany(state, tasksToUpdate);
    //   }
    // });

    // Fill task with FlowAI
    builder.addCase(addFlowAITasks.fulfilled, (state, action) => {
      if (action.payload?.entities?.task) {
        const parentTaskId = action.meta.arg.task_id;
        const newChildren = [
          ...action.payload.result.filter(
            (childId) => action.payload.entities.task[childId].parent_id === parentTaskId
          ),
        ];
        state.entities[action.meta.arg.task_id].children
          ? state.entities[parentTaskId].children.push(newChildren)
          : (state.entities[parentTaskId].children = newChildren);

        tasksAdapter.upsertMany(state, action.payload.entities.task);
      }
    });
    builder.addCase(addFlowAITasks.rejected, (state, action) => {
      state.error = action.payload;
    });

    builder.addMatcher(workspaceAPI.endpoints.setWorkspace.matchFulfilled, (state, action) => {
      tasksAdapter.removeAll(state);
    });
  },
});

export default tasksSlice.reducer;

/** Selectors **/

export const {
  selectById: selectTaskById,
  selectIds: selectTaskIds,
  selectEntities: selectTaskEntities,
  selectAll: selectAllTasks,
  selectTotal: selectTotalTasks,
} = tasksAdapter.getSelectors((state) => state.tasks);

export const selectTaskIdsNoParents = createSelector(
  [(state) => selectAllTasks(state)],
  (tasks) => {
    const allParentIds = tasks.reduce(
      (ids, { parent_id }) => (parent_id !== 0 ? [...ids, parent_id] : ids),
      []
    );
    const ids = tasks.filter((task) => !allParentIds.includes(task.id)).map(({ id }) => id);
    return ids;
  }
);

export const selectSelectedTasks = createSelector([(state) => selectAllTasks(state)], (tasks) =>
  tasks.filter((task) => task.selected).map((task) => task.id)
);

export const selectRemainingTasks = createSelector(
  [
    (state, taskId) => selectTaskById(state, taskId),
    (state) => selectDependencyEntities(state),
    (state) => selectTaskIdsNoParents(state),
    (state) => selectTaskEntities(state),
  ],
  (task, allDependencies, allTaskIds, allTasks) => {
    const selectedTaskId = task?.dependencies?.map((dependencies) => {
      return allDependencies[dependencies]?.source;
    });
    if (selectedTaskId && selectedTaskId.length) {
      const notSelectedTaskId = difference(allTaskIds, selectedTaskId);
      return notSelectedTaskId.map((id) => {
        return allTasks[id];
      });
    } else {
      return allTaskIds.map((id) => {
        return allTasks[id];
      });
    }
  }
);

export const selectTasksInOrder = createSelector(
  [(state) => selectTaskIds(state), (state) => selectTaskEntities(state)],
  (ids, entities) => {
    return ids.map((id) => entities[id]);
  }
);

export const selectAllPlanningTasksByProjectId = createSelector(
  [(state, projectId) => selectTasksInOrder(state), (state, projectId) => projectId],
  (tasks, projectId) => {
    return tasks.filter((task) => !Boolean(task?.is_lookahead) && task?.project_id == projectId);
  }
);

export const selectAllPlanningTasks = createSelector([(state) => selectAllTasks(state)], (tasks) =>
  tasks.filter((task) => !Boolean(task?.is_lookahead))
);

export const selectPlanningTaskEntities = createSelector(
  [(state) => state.tasks.entities],
  (taskEntities) => {
    const filteredEntities = Object.keys(taskEntities).reduce((newObject, taskId) => {
      if (taskEntities[taskId].is_lookahead) {
        newObject[taskId] = taskEntities[taskId];
      }
      return newObject;
    }, {});

    return filteredEntities;
  }
);

export const selectAllProductionBoardTasks = createSelector(
  [(state) => selectAllTasks(state)],
  (tasks) => {
    return tasks.filter((task) => Boolean(task?.is_lookahead));
  }
);

export const selectAllScopeTasks = createSelector([(state) => selectAllTasks(state)], (tasks) =>
  tasks.filter((task) => task.is_scope)
);

export const selectAllChildrenProductionBoardTasks = createSelector(
  selectAllProductionBoardTasks,
  (productionBoardTasks) => productionBoardTasks.filter((task) => !Boolean(task.is_parent))
);

export const selectSiblingProductionBoardTasks = createSelector(
  [
    (state, taskId) => selectTaskById(state, taskId),
    (state) => selectAllProductionBoardTasks(state),
  ],
  (selectedTask, allTasks) => {
    if (!selectedTask) {
      return [];
    }
    return allTasks?.filter((task) => task.parent_id === selectedTask.parent_id);
  }
);

export const selectTaskParentById = createSelector(
  [(state, taskId) => selectTaskById(state, taskId), (state) => selectTaskEntities(state)],
  (task, allTasks) => {
    if (!task) {
      return {};
    }
    return allTasks[task.parent_id];
  }
);

export const selectScopeTaskIdsByScopeId = createSelector(
  [
    (state, scopeId) => selectTaskById(state, scopeId),
    (state) => selectTaskEntities(state),
    (state) => selectAllTasks(state),
  ],
  (scope, taskEntities, allTasks) => {
    if (!scope) {
      return [];
    }
    return allTasks
      .filter((task) => taskEntities[task.parent_id]?.parent_id === scope?.id)
      .map(({ id }) => id);
  }
);

export const selectTasksByParentId = createSelector(
  [(state, parentId) => parentId, (state, parentId) => selectAllTasks(state)],
  (parentId, tasks) => tasks.filter((task) => task.parent_id == parentId)
);

export const selectTaskIdsByParentId = createSelector(
  [(state, parentId) => selectTasksByParentId(state, parentId)],
  (tasks) => tasks.map(({ id }) => id)
);

export const {
  saveTask,
  deleteTask,
  toggleDisplayOption,
  clearSelected,
  setCustomView,
  resetToDefaultFilters,
  clearTasks,
  setPredecessorDialog,
  clearSelectedBaseline,
} = tasksSlice.actions;
